// src/components/events/EventDetail/EventDetailContent.tsx
// Event 詳情內容區：標題、Category Badge、備註

import React, { useState, useCallback } from 'react';
import { Event, EventCategory } from '../../../../types/calendar-event';
import EditableText from '../../../../components/common/EditableText';
import { eventCategoryConfig, eventUnifiedColors } from '../../../../constants/event.constants';
import { CategorySelectorModal } from '../CategorySelectorModal';

interface EventDetailContentProps {
  event: Event;
  onUpdateTitle?: (title: string) => Promise<void>;
  onUpdateNotes?: (notes: string | undefined) => Promise<void>;
  onUpdateCategory?: (category: EventCategory) => Promise<void>;
}

export const EventDetailContent: React.FC<EventDetailContentProps> = ({
  event,
  onUpdateTitle,
  onUpdateNotes,
  onUpdateCategory,
}) => {
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const handleCategoryClick = useCallback(() => {
    if (!onUpdateCategory) return;
    setShowCategoryModal(true);
  }, [onUpdateCategory]);

  const handleCategorySelect = useCallback(async (category: EventCategory) => {
    if (!onUpdateCategory) return;
    setShowCategoryModal(false);
    await onUpdateCategory(category);
  }, [onUpdateCategory]);

  // 取得 Category 配置（防禦性檢查）
  const categoryConfig = event.category ? eventCategoryConfig[event.category] : null;
  const CategoryIcon = categoryConfig?.icon;

  return (
    <div className="flex-1 md:min-w-0 flex flex-col">
      {/* Event Title */}
      <div className="mb-6 px-4">
        {onUpdateTitle ? (
          <EditableText
            value={event.title}
            onSave={onUpdateTitle}
            placeholder="Enter event title..."
            maxLength={100}
            required
            className="text-xl font-semibold text-gray-800"
            inputClassName="text-xl font-semibold text-gray-800"
          />
        ) : (
          <h1 className="text-xl font-semibold text-gray-800 break-words">
            {event.title}
          </h1>
        )}

        {/* Category Badge - 點擊彈出選擇器（Event 有 10 個分類，不適合循環切換） */}
        {categoryConfig && CategoryIcon && (
          <div className="mt-2">
            <button
              onClick={handleCategoryClick}
              disabled={!onUpdateCategory}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${eventUnifiedColors}
                         ${onUpdateCategory ? 'cursor-pointer hover:opacity-80 active:scale-95 transition-all' : 'cursor-default'}`}
              title={onUpdateCategory ? `Category: ${categoryConfig.label} (click to change)` : categoryConfig.label}
              aria-label={`Category: ${categoryConfig.label}${onUpdateCategory ? ', click to change' : ''}`}
            >
              <CategoryIcon size={14} strokeWidth={2.5} />
              <span>{categoryConfig.label}</span>
            </button>
          </div>
        )}
      </div>

      {/* Notes */}
      <section className="mb-6 px-4">
        {onUpdateNotes ? (
          <EditableText
            value={event.notes || event.description || ''}
            onSave={async (value) => {
              await onUpdateNotes(value || undefined);
            }}
            placeholder="Add notes..."
            maxLength={5000}
            multiline
            rows={4}
            minHeight="80px"
            className="text-gray-800"
            inputClassName="text-gray-800"
          />
        ) : (
          (event.notes || event.description) && (
            <p className="text-gray-800 whitespace-pre-line">
              {event.notes || event.description}
            </p>
          )
        )}
      </section>

      {/* Category Selector Modal */}
      {showCategoryModal && (
        <CategorySelectorModal
          currentCategory={event.category}
          onSelect={handleCategorySelect}
          onClose={() => setShowCategoryModal(false)}
        />
      )}
    </div>
  );
};
